import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { FiRefreshCw, FiSearch, FiActivity, FiDollarSign, FiUsers, FiZap } from 'react-icons/fi';
import Layout from '../components/Layout';

interface TalentRun {
  id: number;
  run_id: string;
  run_type: string;
  job: { id: number; title: string } | null;
  user: { id: number; full_name: string; email: string; company: string } | null;
  status: string;
  requested_talents: number;
  found_talents: number;
  credits_consumed: number;
  sourcing_provider: string;
  apify_cost_usd: string;
  gemini_cost_usd: string;
  total_cost_usd: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const money = (v: string | number | null | undefined) => {
  const n = typeof v === 'string' ? parseFloat(v) : (v || 0);
  return `$${(isNaN(n) ? 0 : n).toFixed(4)}`;
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    completed: 'bg-green-100 text-green-700',
    running: 'bg-blue-100 text-blue-700',
    failed: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const TalentFinder = () => {
  const [runs, setRuns] = useState<TalentRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchRuns = async (showRefresh = false) => {
    try {
      showRefresh ? setRefreshing(true) : setLoading(true);
      const params: any = { run_type: 'talent_finder', page: currentPage, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      const response = await apiService.getAnalysisRuns(params);
      if (response.success && response.data) {
        setRuns(response.data.results || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (e) {
      console.error('Error fetching talent finder runs:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, statusFilter]);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Page-level totals for the summary cards.
  const pageApify = runs.reduce((s, r) => s + (parseFloat(r.apify_cost_usd) || 0), 0);
  const pageGemini = runs.reduce((s, r) => s + (parseFloat(r.gemini_cost_usd) || 0), 0);
  const pageCredits = runs.reduce((s, r) => s + (r.credits_consumed || 0), 0);

  const card = (icon: React.ReactNode, label: string, value: string) => (
    <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">{icon}</div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
          <h3 className="text-2xl font-bold text-navy-700 dark:text-white">{value}</h3>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">🎯 Talent Finder Usage</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Sourcing runs with Apify + Gemini cost and credits consumed per execution.
            </p>
          </div>
          <button
            onClick={() => fetchRuns(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-navy-700 px-4 py-2 text-sm font-medium text-white hover:bg-navy-800"
          >
            <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Summary cards (current page) */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          {card(<FiActivity className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />, 'Runs (page)', String(runs.length))}
          {card(<FiDollarSign className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />, 'Apify Cost (page)', money(pageApify))}
          {card(<FiZap className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />, 'Gemini Cost (page)', money(pageGemini))}
          {card(<FiUsers className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />, 'Credits (page)', String(pageCredits))}
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800 md:flex-row md:items-center">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search by user, email, job title, run id…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="running">Running</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Table */}
        <div className="rounded-xl bg-white shadow-sm dark:bg-navy-800">
          <div className="border-b border-gray-200 p-6 dark:border-white/10">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Talent Finder Runs ({totalCount})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500 dark:border-white/10 dark:bg-navy-900 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3 text-center">Req / Found</th>
                  <th className="px-4 py-3 text-right">Apify</th>
                  <th className="px-4 py-3 text-right">Gemini</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-center">Credits</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Completed</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm dark:divide-white/10">
                {loading ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
                ) : runs.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">No talent finder runs yet.</td></tr>
                ) : (
                  runs.map((r) => (
                    <tr key={r.id} className="text-navy-700 dark:text-white">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.user?.full_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.user?.email || ''}</div>
                      </td>
                      <td className="px-4 py-3">{r.job?.title || '—'}</td>
                      <td className="px-4 py-3 text-center">{r.requested_talents} / {r.found_talents}</td>
                      <td className="px-4 py-3 text-right">{money(r.apify_cost_usd)}</td>
                      <td className="px-4 py-3 text-right">{money(r.gemini_cost_usd)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{money(r.total_cost_usd)}</td>
                      <td className="px-4 py-3 text-center font-semibold">{r.credits_consumed}</td>
                      <td className="px-4 py-3 text-xs">{fmtDate(r.started_at)}</td>
                      <td className="px-4 py-3 text-xs">{fmtDate(r.completed_at)}</td>
                      <td className="px-4 py-3 text-center">{statusBadge(r.status)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-white/10">
              <div className="text-sm text-gray-600 dark:text-gray-300">Page {currentPage} of {totalPages}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-white/10 dark:text-white"
                >Previous</button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-white/10 dark:text-white"
                >Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TalentFinder;
